import React from "react";
import {
  makeStyles,
  Box,
  Table,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
} from "@material-ui/core";
import { Row } from "./row";
import { Cell } from "./cell";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "100%",
  },
  table: (props: { virtualized?: boolean }) => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    borderStyle: "solid",
    borderWidth: "1px",
    borderColor: theme.palette.text.disabled,
    boxSizing: "border-box",
    overflow: props.virtualized ? "visible" : "auto",
  }),
  head: {
    display: "block",
    flexGrow: 0,
  },
  body: {
    display: "block",
    flexGrow: 1,
  },
}));

type Props = {
  columns?: {
    label: string;
    width?: number | string;
  }[];
  loading?: boolean;
  rowHeight: number;
  virtualized?: boolean;
};

export const Context = React.createContext<{
  widths: (string | number | undefined)[];
}>({
  widths: [],
});

const Component: React.FC<Props> = (props) => {
  const classes = useStyles({
    virtualized: props.virtualized,
  });
  const widths = React.useMemo(() => {
    return (props.columns || []).map((column) => column?.width);
  }, [props.columns]);
  const children = React.Children.toArray(props.children);
  // console.info(props.loading +" - "+ children.length +" - "+props.virtualized);
  // console.info(children);
  return (
    <Context.Provider value={{ widths }}>
      <TableContainer component="div" className={classes.container}>
        <Table component="div" className={classes.table}>
          {props.columns ? (
            <TableHead component="div" className={classes.head}>
              <Row>
                {props.columns.map((column, index) => (
                  <Cell key={index}>{column.label}</Cell>
                ))}
              </Row>
            </TableHead>
          ) : null}
          <TableBody component="div" className={classes.body}>
            {!props.loading && children.length ? (
              props.virtualized ? (
                <AutoSizer>
                  {({ height, width }) => (
                    <FixedSizeList
                      height={height}
                      width={width}
                      itemCount={children.length}
                      itemSize={props.rowHeight}
                    >
                      {({ index, style }) => {
                        const child = children[index];

                        return React.isValidElement(child)
                          ? React.cloneElement(child, {
                              style,
                            })
                          : null;
                      }}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              ) : (
                children
              )
            ) : (
              <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.disabled"
              >
                <Typography variant="caption">
                  {props.loading ? "loading" : "empty"}
                </Typography>
              </Box>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Context.Provider>
  );
};

export { Component as Table };
